import { DeliveryOption, FulfilmentStore, StoreLocation } from './types';
import { generateDeliveryOptions, getStoreOptions } from '../../availability/utils/availabilityUtils';
import {
  FulfilmentDeliveryResponse,
  FulfilmentOptionsResponse,
  FulfilmentSubBasketLineItem,
  FulfilmentSubBasketStockedLocations,
} from '@jbhi-fi/apis/src/fulfilment/types';
import { StoreAvailabilityDetails } from '../../availability/utils/types';
import { premiumDeliveryOptionValues } from '@jbhi-fi/utils/src/premiumDelivery/premiumDelivery';
import { getStoreClosingTime } from '../../../../utils/store/storeTradingHoursUtils';
import { AlgoliaApiClient } from '../../../../api/localisation/AlgoliaApiClient';
import { parseAlgoliaStoreData } from './parseAlgoliaStoreData';
import { QueryClient } from 'react-query';

const deliveryOptionToFulfilmentStore = ({
  fulfilmentStore,
  isPreOrder,
  storeName = '',
}: {
  fulfilmentStore?: StoreAvailabilityDetails;
  isPreOrder: boolean;
  storeName?: string;
}): FulfilmentStore => {
  if (!fulfilmentStore) {
    return {
      name: storeName ? storeName : '',
      isCncAvailable: false,
      isInStoreAvailable: false,
      isOnlyDisplayAvailable: false,
      messaging: '',
      openUntilMessage: '',
      isStoreOpenInNexthour: false,
      isAwaitingStock: false,
    };
  }
  return {
    name: storeName ? storeName : fulfilmentStore.name || '',
    isCncAvailable: fulfilmentStore?.options.some(option => option.name === 'Click & Collect' && option.isAvailable),
    isInStoreAvailable: isPreOrder
      ? false
      : fulfilmentStore?.options.some(option => option.name === 'In-store' && option.isAvailable),
    isOnlyDisplayAvailable: fulfilmentStore?.options.some(
      option => option.name === 'Display Only' && option.isAvailable
    ),
    messaging: '',
    openUntilMessage: fulfilmentStore.closingTime,
    isStoreOpenInNexthour: fulfilmentStore.isStoreOpenInNexthour,
    isAwaitingStock: fulfilmentStore.isAwaitingStock || false,
  };
};

export const transformDeliveryOptions = ({
  deliveryOptions,
  isBulky = false,
  isBackOrder = false,
}: {
  deliveryOptions: FulfilmentDeliveryResponse;
  isBulky?: boolean;
  isBackOrder?: boolean;
}): Array<DeliveryOption> => {
  const options = generateDeliveryOptions(deliveryOptions)?.options;
  const presentationsSorted = isBulky ? options : options.slice(0, 3);
  return presentationsSorted.map(option => {
    const tag = option.tags?.[0]?.content.toLowerCase();
    return {
      title: option.title,
      isUnavailable: false,
      serviceImage: option.serviceImage ?? '',
      experienceName: option.experienceName ?? '',
      price: option.price ?? 0,
      type: tag === 'cheapest' ? tag : tag === 'fastest' ? tag : undefined,
      messaging: isBulky || isBackOrder ? option.description : '',
    };
  });
};

const getStoresDisplayOnlyStatus = ({
  locationIds,
  stockedLocations,
  shouldShowDisplayModelStatus,
}: {
  locationIds: Array<number>;
  stockedLocations?: Array<FulfilmentSubBasketStockedLocations>;
  shouldShowDisplayModelStatus: boolean;
}): Array<{ locationId: number; isDisplayOnly: boolean; isAwaitingStock?: boolean }> => {
  return locationIds.map(locationId => {
    return {
      locationId,
      isDisplayOnly:
        shouldShowDisplayModelStatus &&
        stockedLocations?.find(location => location.locationId === locationId)?.stockStatus === 'DisplayOnly',
      isAwaitingStock: stockedLocations?.some(
        location => location.locationId === locationId && location.stockStatus === 'AwaitingStock'
      ),
    };
  });
};
const createFulfilmentStoreFromStoreLocation = ({
  store,
  isCnCAvailable = false,
  isInStoreAvailable = false,
  isDisplayOnly = false,
  isAwaitingStock = false,
}: {
  store: StoreLocation;
  isCnCAvailable?: boolean;
  isInStoreAvailable?: boolean;
  isDisplayOnly?: boolean;
  isAwaitingStock?: boolean;
}): StoreAvailabilityDetails => {
  const numericDistance = store.distance ? parseFloat(store.distance.split('km')?.[0]) : 0;
  return {
    id: store.id,
    name: store.name,
    distance: +numericDistance.toFixed(2) || 0,
    closingTime: getStoreClosingTime(new Date(), store.openingHours),
    isAwaitingStock,
    options: [
      ...(isCnCAvailable ? [{ name: 'Click & Collect', isAvailable: isCnCAvailable }] : []),
      ...(isInStoreAvailable ? [{ name: 'In-store', isAvailable: isInStoreAvailable }] : []),
      ...(isDisplayOnly ? [{ name: 'Display Only', isAvailable: isDisplayOnly }] : []),
    ],
  };
};

export const getAggregatedStoreOptions = async ({
  pickupOptions,
  maxDistance,
  shouldShowDisplayModelStatus,
  queryClient,
}: {
  pickupOptions: FulfilmentOptionsResponse;
  maxDistance: number;
  shouldShowDisplayModelStatus: boolean;
  queryClient: QueryClient;
}): Promise<Array<StoreAvailabilityDetails>> => {
  const lineItem = pickupOptions?.clickAndCollect?.subBaskets?.[0]?.lineItems?.[0];
  const stockedLocations = (lineItem as FulfilmentSubBasketLineItem)?.stockedLocations;

  const fulfilmentStoreOptions = getStoreOptions(
    pickupOptions?.clickAndCollect?.clickAndCollectOptions,
    pickupOptions?.clickAndCollect?.presentation,
    pickupOptions?.cashAndCarry?.cashAndCarryOptions,
    maxDistance,
    stockedLocations
  );
  // Get the closest store's approximate location
  const closestStore = pickupOptions?.clickAndCollect?.closestLocation;
  const approximateLocation = closestStore?.address?.coordinates;

  // Fetch nearby stores from Algolia API based on approximate location
  const nearbyStoreResults = approximateLocation
    ? (await queryClient.fetchQuery({
        queryKey: ['getAggregatedStoreOptions', 'nearbyStores', approximateLocation],
        queryFn: () => AlgoliaApiClient.getStores({ query: '', approximateLocation, hitsPerPage: 75 }),
        cacheTime: 30 * 60 * 1000,
        staleTime: 30 * 60 * 1000,
      })) ?? []
    : [];

  const parsedNearbyStores = nearbyStoreResults.map(store => {
    return parseAlgoliaStoreData(store, approximateLocation);
  });

  // Get display status of stores based on location and display stock availability
  const storesDisplayOnlyStatus = getStoresDisplayOnlyStatus({
    locationIds: parsedNearbyStores.map(store => store.id),
    stockedLocations: (
      pickupOptions?.clickAndCollect?.subBaskets?.[0]?.lineItems?.[0] as FulfilmentSubBasketLineItem | undefined
    )?.stockedLocations,
    shouldShowDisplayModelStatus,
  });

  // Get IDs of fulfilment store locations
  const fulfilmentLocationIds = fulfilmentStoreOptions.map(store => store.id);

  // Get IDs of stores with display only
  const displayStoreIds = storesDisplayOnlyStatus
    .map(store => {
      return store.isDisplayOnly ? store.locationId : null;
    })
    .filter(Number);

  // Filter and map Algolia stores with display stock, excluding fulfilment locations to remove duplicates
  const algoliaStoresWithDisplayStock = parsedNearbyStores
    .filter(store => !fulfilmentLocationIds.includes(store.id) && displayStoreIds.includes(store.id))
    .map(store => {
      return createFulfilmentStoreFromStoreLocation({
        store,
        isDisplayOnly: storesDisplayOnlyStatus.find(location => location.locationId === store.id)?.isDisplayOnly,
        isAwaitingStock: storesDisplayOnlyStatus.find(location => location.locationId === store.id)?.isAwaitingStock,
      });
    });

  // Combine fulfilment and Algolia stores, then sort by distance
  return fulfilmentStoreOptions
    .concat(algoliaStoresWithDisplayStock)
    .sort((store1, store2) => store1.distance - store2.distance);
};

export const transformPickupOptions = async ({
  pickupOptions,
  selectedStore,
  isPreOrder,
  maxDistance,
  shouldShowDisplayModelStatus,
  queryClient,
}: {
  pickupOptions: FulfilmentOptionsResponse;
  selectedStore: StoreLocation;
  isPreOrder: boolean;
  isBulky: boolean;
  isBackOrder: boolean;
  maxDistance: number;
  shouldShowDisplayModelStatus: boolean;
  queryClient: QueryClient;
}): Promise<{
  yourStore?: FulfilmentStore;
  nextClosestStore?: FulfilmentStore;
  isAvailableOutOfRange: boolean;
  importantMessaging?: string;
}> => {
  const storeOptions = await getAggregatedStoreOptions({
    pickupOptions,
    maxDistance,
    shouldShowDisplayModelStatus,
    queryClient,
  });
  const yourStore = storeOptions.find(store => store.id === selectedStore?.id);
  const otherStores = storeOptions.filter(store => store.id !== selectedStore?.id);
  const nextClosestStore = otherStores[0];

  const isAvailableOptionsAtAnyRange =
    pickupOptions?.clickAndCollect?.clickAndCollectOptions?.length ||
    pickupOptions?.cashAndCarry?.cashAndCarryOptions?.length;

  const formatterdYourStore = deliveryOptionToFulfilmentStore({
    fulfilmentStore: yourStore,
    isPreOrder,
    storeName: selectedStore?.name,
  });
  const formattedNextClosestStore = deliveryOptionToFulfilmentStore({
    fulfilmentStore: nextClosestStore,
    isPreOrder,
  });

  const isPickupAvailableNearby =
    formattedNextClosestStore?.isCncAvailable ||
    formattedNextClosestStore?.isInStoreAvailable ||
    formattedNextClosestStore?.isOnlyDisplayAvailable;
  const isPickupOptionsAvailable =
    formatterdYourStore.isCncAvailable ||
    formatterdYourStore.isInStoreAvailable ||
    formatterdYourStore.isOnlyDisplayAvailable;
  const shouldDisplayNearbyOptions = !isPickupOptionsAvailable && isPickupAvailableNearby;
  return {
    yourStore: formatterdYourStore,
    nextClosestStore: formattedNextClosestStore,
    isAvailableOutOfRange: !!(isAvailableOptionsAtAnyRange && (!storeOptions || storeOptions.length === 0)) ?? false,
    importantMessaging: !shouldDisplayNearbyOptions
      ? yourStore?.importantMessaging
      : nextClosestStore?.importantMessaging,
  };
};

export const getIsPremiumDeliveryAvailable = (parsedOptions: Array<DeliveryOption>) => {
  return parsedOptions?.some(option => premiumDeliveryOptionValues.includes(option.experienceName));
};

const getNumberArrayFromString = (string: string) => {
  const numberArray = string
    ? string?.split(',').map((item: string) => {
        const number = parseInt(item.trim());
        return !Number.isNaN(number) ? number : null;
      })
    : [];
  return numberArray.filter(Number);
};

export const getIsPdpLocalisationEnabled = ({
  productGroup,
  isLocalisationEnabled,
  excludedProductGroups,
}: {
  productGroup?: number;
  isLocalisationEnabled?: boolean;
  excludedProductGroups: string;
}) => {
  const excludedProductGroupsArray = excludedProductGroups ? getNumberArrayFromString(excludedProductGroups) : [];
  const isProductGroupExcludedFromLocalisation = productGroup
    ? excludedProductGroupsArray?.includes(productGroup)
    : false;

  return isLocalisationEnabled && !isProductGroupExcludedFromLocalisation;
};

export const getShouldShowLocalisationDisplayModelStatus = ({
  productGroup,
  displayModelProductGroups,
}: {
  productGroup?: number;
  displayModelProductGroups: string;
}) => {
  const displayModelProductGroupsArray = displayModelProductGroups
    ? getNumberArrayFromString(displayModelProductGroups)
    : [];
  return productGroup ? displayModelProductGroupsArray?.includes(productGroup) : false;
};
